/**
 * Generated by orval 🍺
 * Do not edit manually.
 * MTP Service Mobile API
 * API REST pour l'espace client mobile - Migration Symfony 7.3
 * OpenAPI spec version: 2.0.0
 */
import { useMutation } from '@tanstack/react-query';
import type { MutationFunction, QueryClient, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

import type {
  AdvisorConnectRequest,
  AdvisorConnectResponse,
  AdvisorLogin200,
  AdvisorLogin401,
  AdvisorLoginBody,
  Auth0ChangePasswordRequest,
  Auth0CreateRequest,
  Auth0CredentialsRequest,
  Auth0CredentialsResponse,
  Auth0GetUserRequest,
  InvalidateToken200,
  InvalidateToken401,
  InvalidateTokenBody,
  PostAppAuthenticationAdvisorconnectInvoke401,
  RefreshToken200,
  RefreshToken401,
  RefreshTokenBody,
  UserLogin200,
  UserLogin401,
  UserLoginBody,
} from '../placementApi.schemas.ts';

import userLoginMutator from '.././mutator/placement-instance.ts';
import advisorLoginMutator from '.././mutator/placement-instance.ts';
import refreshTokenMutator from '.././mutator/placement-instance.ts';
import invalidateTokenMutator from '.././mutator/placement-instance.ts';
import postAppAuthenticationAdvisorconnectInvokeMutator from '.././mutator/placement-instance.ts';
import postAppAuthenticationAuth0changepasswordInvokeMutator from '.././mutator/placement-instance.ts';
import postAppAuthenticationAuth0createInvokeMutator from '.././mutator/placement-instance.ts';
import postAppAuthenticationAuth0credentialsInvokeMutator from '.././mutator/placement-instance.ts';
import postAppAuthenticationAuth0getuserInvokeMutator from '.././mutator/placement-instance.ts';

/**
 * Authenticate a user with their Salesforce credentials.
 *
 * **Behavior varies by client type (header `X-Client-Type`):**
 *
 * - **`mobile`** (default): returns `token` and `refresh_token` in the response body.
 * - **`web`**: returns HTTP 204 with no body. Tokens are set as httpOnly cookies (`BEARER`, `refresh_token`).
 * @summary User login with Salesforce credentials
 */
export const userLogin = (userLoginBody: UserLoginBody, signal?: AbortSignal) => {
  return userLoginMutator<UserLogin200 | void>({
    url: `/api/authentication/login`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: userLoginBody,
    signal,
  });
};

export const getUserLoginMutationKey = () => ['userLogin'] as const;

export const getUserLoginMutationOptions = <TError = UserLogin401, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof userLogin>>, TError, UserLoginMutationVariables, TContext>;
}): UseMutationOptions<Awaited<ReturnType<typeof userLogin>>, TError, UserLoginMutationVariables, TContext> => {
  const mutationKey = getUserLoginMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof userLogin>>, UserLoginMutationVariables> = (props) => {
    const { data } = props ?? {};

    return userLogin(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UserLoginMutationResult = NonNullable<Awaited<ReturnType<typeof userLogin>>>;
export type UserLoginMutationBody = UserLoginBody;
export type UserLoginMutationError = UserLogin401;
export type UserLoginMutationVariables = { data: UserLoginBody };

/**
 * @summary User login with Salesforce credentials
 */
export const useUserLogin = <TError = UserLogin401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof userLogin>>, TError, UserLoginMutationVariables, TContext>;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof userLogin>>, TError, UserLoginMutationVariables, TContext> => {
  return useMutation(getUserLoginMutationOptions(options), queryClient);
};
/**
 * Authenticate an advisor for the advisor view
 * @summary Advisor login
 */
export const advisorLogin = (advisorLoginBody: AdvisorLoginBody, signal?: AbortSignal) => {
  return advisorLoginMutator<AdvisorLogin200>({
    url: `/api/authentication/advisor`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: advisorLoginBody,
    signal,
  });
};

export const getAdvisorLoginMutationKey = () => ['advisorLogin'] as const;

export const getAdvisorLoginMutationOptions = <TError = AdvisorLogin401, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof advisorLogin>>,
    TError,
    AdvisorLoginMutationVariables,
    TContext
  >;
}): UseMutationOptions<Awaited<ReturnType<typeof advisorLogin>>, TError, AdvisorLoginMutationVariables, TContext> => {
  const mutationKey = getAdvisorLoginMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof advisorLogin>>, AdvisorLoginMutationVariables> = (
    props
  ) => {
    const { data } = props ?? {};

    return advisorLogin(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdvisorLoginMutationResult = NonNullable<Awaited<ReturnType<typeof advisorLogin>>>;
export type AdvisorLoginMutationBody = AdvisorLoginBody;
export type AdvisorLoginMutationError = AdvisorLogin401;
export type AdvisorLoginMutationVariables = { data: AdvisorLoginBody };

/**
 * @summary Advisor login
 */
export const useAdvisorLogin = <TError = AdvisorLogin401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof advisorLogin>>,
      TError,
      AdvisorLoginMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof advisorLogin>>, TError, AdvisorLoginMutationVariables, TContext> => {
  return useMutation(getAdvisorLoginMutationOptions(options), queryClient);
};
/**
 * Refresh an expired JWT token using a valid refresh token (single-use).
 *
 * **Behavior varies by client type (header `X-Client-Type`):**
 *
 * - **`mobile`**: send `refresh_token` in the request body, receive new tokens in the response body.
 * - **`web`**: send the `refresh_token` cookie automatically (browser), receive HTTP 204 with new httpOnly cookies.
 * @summary Refresh JWT token
 */
export const refreshToken = (refreshTokenBody?: RefreshTokenBody, signal?: AbortSignal) => {
  return refreshTokenMutator<RefreshToken200 | void>({
    url: `/token/refresh`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: refreshTokenBody,
    signal,
  });
};

export const getRefreshTokenMutationKey = () => ['refreshToken'] as const;

export const getRefreshTokenMutationOptions = <TError = RefreshToken401, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshToken>>,
    TError,
    RefreshTokenMutationVariables,
    TContext
  >;
}): UseMutationOptions<Awaited<ReturnType<typeof refreshToken>>, TError, RefreshTokenMutationVariables, TContext> => {
  const mutationKey = getRefreshTokenMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshToken>>, RefreshTokenMutationVariables> = (
    props
  ) => {
    const { data } = props ?? {};

    return refreshToken(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshTokenMutationResult = NonNullable<Awaited<ReturnType<typeof refreshToken>>>;
export type RefreshTokenMutationBody = RefreshTokenBody | undefined;
export type RefreshTokenMutationError = RefreshToken401;
export type RefreshTokenMutationVariables = { data?: RefreshTokenBody };

/**
 * @summary Refresh JWT token
 */
export const useRefreshToken = <TError = RefreshToken401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshToken>>,
      TError,
      RefreshTokenMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof refreshToken>>, TError, RefreshTokenMutationVariables, TContext> => {
  return useMutation(getRefreshTokenMutationOptions(options), queryClient);
};
/**
 * Invalidate the current refresh token to logout the user
 * @summary Invalidate (logout) refresh token
 */
export const invalidateToken = (invalidateTokenBody: InvalidateTokenBody, signal?: AbortSignal) => {
  return invalidateTokenMutator<InvalidateToken200>({
    url: `/token/invalidate`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: invalidateTokenBody,
    signal,
  });
};

export const getInvalidateTokenMutationKey = () => ['invalidateToken'] as const;

export const getInvalidateTokenMutationOptions = <TError = InvalidateToken401, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof invalidateToken>>,
    TError,
    InvalidateTokenMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof invalidateToken>>,
  TError,
  InvalidateTokenMutationVariables,
  TContext
> => {
  const mutationKey = getInvalidateTokenMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof invalidateToken>>, InvalidateTokenMutationVariables> = (
    props
  ) => {
    const { data } = props ?? {};

    return invalidateToken(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InvalidateTokenMutationResult = NonNullable<Awaited<ReturnType<typeof invalidateToken>>>;
export type InvalidateTokenMutationBody = InvalidateTokenBody;
export type InvalidateTokenMutationError = InvalidateToken401;
export type InvalidateTokenMutationVariables = { data: InvalidateTokenBody };

/**
 * @summary Invalidate (logout) refresh token
 */
export const useInvalidateToken = <TError = InvalidateToken401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof invalidateToken>>,
      TError,
      InvalidateTokenMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof invalidateToken>>,
  TError,
  InvalidateTokenMutationVariables,
  TContext
> => {
  return useMutation(getInvalidateTokenMutationOptions(options), queryClient);
};
/**
 * Valide le token conseiller pour accorder l'acces aux donnees client.
 * @summary Validation du token conseiller pour l'acces aux donnees client
 */
export const postAppAuthenticationAdvisorconnectInvoke = (
  advisorConnectRequest: AdvisorConnectRequest,
  signal?: AbortSignal
) => {
  return postAppAuthenticationAdvisorconnectInvokeMutator<AdvisorConnectResponse>({
    url: `/api/authentication/advisorconnect`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: advisorConnectRequest,
    signal,
  });
};

export const getPostAppAuthenticationAdvisorconnectInvokeMutationKey = () =>
  ['postAppAuthenticationAdvisorconnectInvoke'] as const;

export const getPostAppAuthenticationAdvisorconnectInvokeMutationOptions = <
  TError = PostAppAuthenticationAdvisorconnectInvoke401,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>,
    TError,
    PostAppAuthenticationAdvisorconnectInvokeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>,
  TError,
  PostAppAuthenticationAdvisorconnectInvokeMutationVariables,
  TContext
> => {
  const mutationKey = getPostAppAuthenticationAdvisorconnectInvokeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>,
    PostAppAuthenticationAdvisorconnectInvokeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return postAppAuthenticationAdvisorconnectInvoke(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAppAuthenticationAdvisorconnectInvokeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>
>;
export type PostAppAuthenticationAdvisorconnectInvokeMutationBody = AdvisorConnectRequest;
export type PostAppAuthenticationAdvisorconnectInvokeMutationError = PostAppAuthenticationAdvisorconnectInvoke401;
export type PostAppAuthenticationAdvisorconnectInvokeMutationVariables = { data: AdvisorConnectRequest };

/**
 * @summary Validation du token conseiller pour l'acces aux donnees client
 */
export const usePostAppAuthenticationAdvisorconnectInvoke = <
  TError = PostAppAuthenticationAdvisorconnectInvoke401,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>,
      TError,
      PostAppAuthenticationAdvisorconnectInvokeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postAppAuthenticationAdvisorconnectInvoke>>,
  TError,
  PostAppAuthenticationAdvisorconnectInvokeMutationVariables,
  TContext
> => {
  return useMutation(getPostAppAuthenticationAdvisorconnectInvokeMutationOptions(options), queryClient);
};
/**
 * Appelé par le script Custom Database d'Auth0 : change le mot de passe d'un contact déjà activé.
 * @summary Changement de mot de passe pour Auth0
 */
export const postAppAuthenticationAuth0changepasswordInvoke = (
  auth0ChangePasswordRequest: Auth0ChangePasswordRequest,
  signal?: AbortSignal
) => {
  return postAppAuthenticationAuth0changepasswordInvokeMutator<void>({
    url: `/api/auth0/change-password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: auth0ChangePasswordRequest,
    signal,
  });
};

export const getPostAppAuthenticationAuth0changepasswordInvokeMutationKey = () =>
  ['postAppAuthenticationAuth0changepasswordInvoke'] as const;

export const getPostAppAuthenticationAuth0changepasswordInvokeMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>,
    TError,
    PostAppAuthenticationAuth0changepasswordInvokeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>,
  TError,
  PostAppAuthenticationAuth0changepasswordInvokeMutationVariables,
  TContext
> => {
  const mutationKey = getPostAppAuthenticationAuth0changepasswordInvokeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>,
    PostAppAuthenticationAuth0changepasswordInvokeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return postAppAuthenticationAuth0changepasswordInvoke(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAppAuthenticationAuth0changepasswordInvokeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>
>;
export type PostAppAuthenticationAuth0changepasswordInvokeMutationBody = Auth0ChangePasswordRequest;
export type PostAppAuthenticationAuth0changepasswordInvokeMutationError = void;
export type PostAppAuthenticationAuth0changepasswordInvokeMutationVariables = { data: Auth0ChangePasswordRequest };

/**
 * @summary Changement de mot de passe pour Auth0
 */
export const usePostAppAuthenticationAuth0changepasswordInvoke = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>,
      TError,
      PostAppAuthenticationAuth0changepasswordInvokeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0changepasswordInvoke>>,
  TError,
  PostAppAuthenticationAuth0changepasswordInvokeMutationVariables,
  TContext
> => {
  return useMutation(getPostAppAuthenticationAuth0changepasswordInvokeMutationOptions(options), queryClient);
};
/**
 * Appelé par le script Custom Database d'Auth0 : crée le contact ou active le mot de passe d'un contact existant.
 * @summary Inscription d'un compte pour Auth0
 */
export const postAppAuthenticationAuth0createInvoke = (
  auth0CreateRequest: Auth0CreateRequest,
  signal?: AbortSignal
) => {
  return postAppAuthenticationAuth0createInvokeMutator<void>({
    url: `/api/auth0/create`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: auth0CreateRequest,
    signal,
  });
};

export const getPostAppAuthenticationAuth0createInvokeMutationKey = () =>
  ['postAppAuthenticationAuth0createInvoke'] as const;

export const getPostAppAuthenticationAuth0createInvokeMutationOptions = <TError = void, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>,
    TError,
    PostAppAuthenticationAuth0createInvokeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>,
  TError,
  PostAppAuthenticationAuth0createInvokeMutationVariables,
  TContext
> => {
  const mutationKey = getPostAppAuthenticationAuth0createInvokeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>,
    PostAppAuthenticationAuth0createInvokeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return postAppAuthenticationAuth0createInvoke(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAppAuthenticationAuth0createInvokeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>
>;
export type PostAppAuthenticationAuth0createInvokeMutationBody = Auth0CreateRequest;
export type PostAppAuthenticationAuth0createInvokeMutationError = void;
export type PostAppAuthenticationAuth0createInvokeMutationVariables = { data: Auth0CreateRequest };

/**
 * @summary Inscription d'un compte pour Auth0
 */
export const usePostAppAuthenticationAuth0createInvoke = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>,
      TError,
      PostAppAuthenticationAuth0createInvokeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0createInvoke>>,
  TError,
  PostAppAuthenticationAuth0createInvokeMutationVariables,
  TContext
> => {
  return useMutation(getPostAppAuthenticationAuth0createInvokeMutationOptions(options), queryClient);
};
/**
 * Appelé par le script Custom Database d'Auth0 : valide identifiant et mot de passe auprès de Salesforce.
 * @summary Validation des identifiants pour Auth0
 */
export const postAppAuthenticationAuth0credentialsInvoke = (
  auth0CredentialsRequest: Auth0CredentialsRequest,
  signal?: AbortSignal
) => {
  return postAppAuthenticationAuth0credentialsInvokeMutator<Auth0CredentialsResponse>({
    url: `/api/auth0/credentials`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: auth0CredentialsRequest,
    signal,
  });
};

export const getPostAppAuthenticationAuth0credentialsInvokeMutationKey = () =>
  ['postAppAuthenticationAuth0credentialsInvoke'] as const;

export const getPostAppAuthenticationAuth0credentialsInvokeMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>,
    TError,
    PostAppAuthenticationAuth0credentialsInvokeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>,
  TError,
  PostAppAuthenticationAuth0credentialsInvokeMutationVariables,
  TContext
> => {
  const mutationKey = getPostAppAuthenticationAuth0credentialsInvokeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>,
    PostAppAuthenticationAuth0credentialsInvokeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return postAppAuthenticationAuth0credentialsInvoke(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAppAuthenticationAuth0credentialsInvokeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>
>;
export type PostAppAuthenticationAuth0credentialsInvokeMutationBody = Auth0CredentialsRequest;
export type PostAppAuthenticationAuth0credentialsInvokeMutationError = void;
export type PostAppAuthenticationAuth0credentialsInvokeMutationVariables = { data: Auth0CredentialsRequest };

/**
 * @summary Validation des identifiants pour Auth0
 */
export const usePostAppAuthenticationAuth0credentialsInvoke = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>,
      TError,
      PostAppAuthenticationAuth0credentialsInvokeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0credentialsInvoke>>,
  TError,
  PostAppAuthenticationAuth0credentialsInvokeMutationVariables,
  TContext
> => {
  return useMutation(getPostAppAuthenticationAuth0credentialsInvokeMutationOptions(options), queryClient);
};
/**
 * Appelé par le script Custom Database d'Auth0 : renvoie le compte seulement s'il a déjà un mot de passe.
 * @summary Lecture d'un compte activé pour Auth0
 */
export const postAppAuthenticationAuth0getuserInvoke = (
  auth0GetUserRequest: Auth0GetUserRequest,
  signal?: AbortSignal
) => {
  return postAppAuthenticationAuth0getuserInvokeMutator<Auth0CredentialsResponse>({
    url: `/api/auth0/get-user`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: auth0GetUserRequest,
    signal,
  });
};

export const getPostAppAuthenticationAuth0getuserInvokeMutationKey = () =>
  ['postAppAuthenticationAuth0getuserInvoke'] as const;

export const getPostAppAuthenticationAuth0getuserInvokeMutationOptions = <TError = void, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>,
    TError,
    PostAppAuthenticationAuth0getuserInvokeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>,
  TError,
  PostAppAuthenticationAuth0getuserInvokeMutationVariables,
  TContext
> => {
  const mutationKey = getPostAppAuthenticationAuth0getuserInvokeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>,
    PostAppAuthenticationAuth0getuserInvokeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return postAppAuthenticationAuth0getuserInvoke(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAppAuthenticationAuth0getuserInvokeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>
>;
export type PostAppAuthenticationAuth0getuserInvokeMutationBody = Auth0GetUserRequest;
export type PostAppAuthenticationAuth0getuserInvokeMutationError = void;
export type PostAppAuthenticationAuth0getuserInvokeMutationVariables = { data: Auth0GetUserRequest };

/**
 * @summary Lecture d'un compte activé pour Auth0
 */
export const usePostAppAuthenticationAuth0getuserInvoke = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>,
      TError,
      PostAppAuthenticationAuth0getuserInvokeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postAppAuthenticationAuth0getuserInvoke>>,
  TError,
  PostAppAuthenticationAuth0getuserInvokeMutationVariables,
  TContext
> => {
  return useMutation(getPostAppAuthenticationAuth0getuserInvokeMutationOptions(options), queryClient);
};
